import { useEffect, useLayoutEffect, useRef, useState } from "react";

const WAVE_PULSES = [
  { x: 447, width: 98 },
  { x: 545, width: 180 },
  { x: 725, width: 205 },
  { x: 930, width: 215 },
  { x: 1145, width: 245 },
  { x: 1390, width: 321 },
];

export default function LandingLoadScreen({ onExitStart }) {
  const [phase, setPhase] = useState("visible");
  const [logoState, setLogoState] = useState("waiting");
  const phaseRef = useRef("visible");
  const loadedAssetsRef = useRef(new Set());

  const markAssetLoaded = (asset) => {
    if (phaseRef.current !== "visible") return;

    loadedAssetsRef.current.add(asset);
    if (loadedAssetsRef.current.size === 2) setLogoState("ready");
  };

  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const exitDelay = logoState === "waiting" ? 1700 : logoState === "failed" ? 0 : reduceMotion ? 160 : 2180;
    const removeDelay = logoState === "waiting" ? 2030 : logoState === "failed" ? 330 : reduceMotion ? 300 : 2510;
    const exitTimer = window.setTimeout(() => {
      phaseRef.current = "leaving";
      setPhase("leaving");
      onExitStart?.();
    }, exitDelay);
    const removeTimer = window.setTimeout(() => {
      phaseRef.current = "removed";
      setPhase("removed");
    }, removeDelay);

    return () => {
      window.clearTimeout(exitTimer);
      window.clearTimeout(removeTimer);
    };
  }, [logoState, onExitStart]);

  useEffect(() => {
    if (phase === "removed") return undefined;

    const landingRoot = document.getElementById("top");
    if (!landingRoot) return undefined;

    const wasInert = landingRoot.inert;
    landingRoot.inert = true;

    return () => {
      landingRoot.inert = wasInert;
    };
  }, [phase]);

  useLayoutEffect(() => {
    if (phase === "removed") return undefined;

    const documentElement = document.documentElement;
    const { body } = document;
    const previousDocumentOverflow = documentElement.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousOverscrollBehavior = documentElement.style.overscrollBehavior;

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    documentElement.style.overflow = "hidden";
    body.style.overflow = "hidden";
    documentElement.style.overscrollBehavior = "none";

    return () => {
      documentElement.style.overflow = previousDocumentOverflow;
      body.style.overflow = previousBodyOverflow;
      documentElement.style.overscrollBehavior = previousOverscrollBehavior;
    };
  }, [phase]);

  if (phase === "removed") {
    return null;
  }

  return (
    <div className={`landing-load-screen${phase === "leaving" ? " is-leaving" : ""}`} aria-hidden="true">
      <img
        className="landing-load-screen__preload"
        src="/landing/min-loader-wave.png"
        alt=""
        onLoad={() => markAssetLoaded("wave")}
        onError={() => {
          if (phaseRef.current === "visible") setLogoState("failed");
        }}
      />
      <img
        className="landing-load-screen__preload"
        src="/landing/min-loader-word.png"
        alt=""
        onLoad={() => markAssetLoaded("word")}
        onError={() => {
          if (phaseRef.current === "visible") setLogoState("failed");
        }}
      />
      <svg className={`landing-load-screen__logo is-${logoState}`} viewBox="0 0 548 326" role="presentation">
        <svg className="landing-load-screen__waveform" x="0" y="0" width="240" height="326" viewBox="447 1002 1264 1714" preserveAspectRatio="none">
          <defs>
            {WAVE_PULSES.map((pulse, index) => (
              <clipPath id={`landing-load-wave-pulse-${index}`} clipPathUnits="userSpaceOnUse" key={pulse.x}>
                <rect x={pulse.x} y="1002" width={pulse.width} height="1714" />
              </clipPath>
            ))}
          </defs>
          {WAVE_PULSES.map((pulse, index) => (
            <g
              className="landing-load-screen__wave-pulse"
              clipPath={`url(#landing-load-wave-pulse-${index})`}
              key={pulse.x}
              style={{ "--landing-load-wave-index": index }}
            >
              <image
                className="landing-load-screen__source-art"
                href="/landing/min-loader-wave.png"
                width="3750"
                height="3750"
              />
            </g>
          ))}
        </svg>
        <svg className="landing-load-screen__wordmark" x="231" y="99" width="308" height="152" viewBox="1675 1512 1654 815" preserveAspectRatio="none">
          <image
            className="landing-load-screen__source-art landing-load-screen__wordmark-reveal"
            href="/landing/min-loader-word.png"
            width="3750"
            height="3750"
          />
        </svg>
      </svg>
    </div>
  );
}
